import { createClient, SupabaseClient } from '@supabase/supabase-js'

// Types
export interface DailyPrompt {
  id: string
  date: string
  spectrum_a_label: string
  spectrum_b_label: string
  spectrum_a_left: string
  spectrum_a_right: string
  spectrum_b_left: string
  spectrum_b_right: string
  crowd_a_avg: number | null
  crowd_b_avg: number | null
  crowd_a_count: number
  crowd_b_count: number
  is_active: boolean
  created_at: string
}

export interface UserProfile {
  id: string
  username: string | null
  display_name: string | null
  avatar_url: string | null
  current_streak: number
  longest_streak: number
  total_plays: number
  freezes_earned: number
  freezes_used: number
  created_at: string
  updated_at: string
}

export interface DailyResponse {
  id: string
  user_id: string
  prompt_id: string
  spectrum_a_value: number
  spectrum_b_value: number
  crowd_a_avg_at_submit: number | null
  crowd_b_avg_at_submit: number | null
  score_a: number | null
  score_b: number | null
  total_score: number | null
  submitted_at: string
  created_at: string
}

export interface Duel {
  id: string
  creator_id: string
  opponent_id: string | null
  prompt_id: string
  invite_code: string
  creator_spectrum_a: number | null
  creator_spectrum_b: number | null
  opponent_spectrum_a: number | null
  opponent_spectrum_b: number | null
  crowd_a_avg: number | null
  crowd_b_avg: number | null
  sync_percentage: number | null
  status: 'waiting' | 'playing' | 'completed' | 'expired'
  expires_at: string
  completed_at: string | null
  created_at: string
  updated_at: string | null
  // Joined fields from prompt and user tables
  prompt?: DailyPrompt | null
  creator_username?: string | null
  opponent_username?: string | null
}

export interface UGCSubmission {
  id: string
  user_id: string
  spectrum_a_label: string
  spectrum_b_label: string
  spectrum_a_left: string
  spectrum_a_right: string
  spectrum_b_left: string
  spectrum_b_right: string
  status: 'pending' | 'approved' | 'rejected' | 'used'
  admin_notes: string | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
}

export interface Freeze {
  id: string
  user_id: string
  earned_at: string
  used_at: string | null
  source: 'streak_milestone' | 'purchase' | 'admin_grant'
}

export interface ShareCard {
  id: string
  user_id: string
  prompt_id: string
  image_url: string
  created_at: string
}

// Helper functions
export function isSubmitted(value: number | null | undefined): boolean {
  return value !== null && value !== undefined
}

export function calculateScore(userValue: number, crowdAvg: number): number {
  const diff = Math.abs(userValue - crowdAvg)
  return Math.max(0, 100 - diff)
}

export function calculateSyncPercentage(userA: number, userB: number): number {
  const diff = Math.abs(userA - userB)
  return Math.max(0, 100 - diff)
}

export function calculateVibe(userValue: number, crowdAvg: number): 'weirdo' | 'basic' | 'in sync' {
  if (userValue > crowdAvg + 15) return 'weirdo'
  if (userValue < crowdAvg - 15) return 'basic'
  return 'in sync'
}

const DEMO_PROMPTS: DailyPrompt[] = [
  {
    id: 'demo-1',
    date: new Date().toISOString().split('T')[0],
    spectrum_a_label: 'Plane Clapping',
    spectrum_b_label: 'Vibe Check',
    spectrum_a_left: 'Not a red flag',
    spectrum_a_right: 'Dealbreaker',
    spectrum_b_left: 'Weirdo',
    spectrum_b_right: 'Basic',
    crowd_a_avg: 67,
    crowd_b_avg: 42,
    crowd_a_count: 1247,
    crowd_b_count: 1247,
    is_active: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'demo-2',
    date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Pineapple on Pizza',
    spectrum_b_label: 'Hot Dog = Sandwich',
    spectrum_a_left: 'Crime',
    spectrum_a_right: 'Delicious',
    spectrum_b_left: 'Absolutely Not',
    spectrum_b_right: 'Technically Yes',
    crowd_a_avg: 34,
    crowd_b_avg: 78,
    crowd_a_count: 2103,
    crowd_b_count: 2103,
    is_active: false,
    created_at: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'demo-3',
    date: new Date(Date.now() - 2 * 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Texting "k"',
    spectrum_b_label: 'Shower Temperature',
    spectrum_a_left: 'Efficient',
    spectrum_a_right: 'Passive aggressive',
    spectrum_b_left: 'Freezing',
    spectrum_b_right: 'Scalding',
    crowd_a_avg: 72,
    crowd_b_avg: 58,
    crowd_a_count: 892,
    crowd_b_count: 892,
    is_active: false,
    created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'demo-4',
    date: new Date(Date.now() - 3 * 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Cereal before milk',
    spectrum_b_label: 'Toilet Paper Orientation',
    spectrum_a_left: 'Correct',
    spectrum_a_right: 'Chaos',
    spectrum_b_left: 'Over',
    spectrum_b_right: 'Under',
    crowd_a_avg: 23,
    crowd_b_avg: 84,
    crowd_a_count: 1567,
    crowd_b_count: 1567,
    is_active: false,
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: 'demo-5',
    date: new Date(Date.now() - 4 * 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Double dipping chips',
    spectrum_b_label: 'Reading last page first',
    spectrum_a_left: 'Normal',
    spectrum_a_right: 'Crime against humanity',
    spectrum_b_left: 'Spoil yourself',
    spectrum_b_right: 'Preserve the journey',
    crowd_a_avg: 61,
    crowd_b_avg: 39,
    crowd_a_count: 743,
    crowd_b_count: 743,
    is_active: false,
    created_at: new Date(Date.now() - 4 * 86400000).toISOString(),
  },
  {
    id: 'demo-6',
    date: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Socks with sandals',
    spectrum_b_label: 'Clapping when movie ends',
    spectrum_a_left: 'Comfort > style',
    spectrum_a_right: 'Fashion crime',
    spectrum_b_left: 'Appreciation',
    spectrum_b_right: 'Cringe',
    crowd_a_avg: 45,
    crowd_b_avg: 71,
    crowd_a_count: 1123,
    crowd_b_count: 1123,
    is_active: false,
    created_at: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: 'demo-7',
    date: new Date(Date.now() - 6 * 86400000).toISOString().split('T')[0],
    spectrum_a_label: 'Putting ketchup on hot dog',
    spectrum_b_label: 'Making bed daily',
    spectrum_a_left: 'Acceptable',
    spectrum_a_right: 'Chicago would disagree',
    spectrum_b_left: 'Discipline',
    spectrum_b_right: 'Pointless',
    crowd_a_avg: 38,
    crowd_b_avg: 52,
    crowd_a_count: 987,
    crowd_b_count: 987,
    is_active: false,
    created_at: new Date(Date.now() - 6 * 86400000).toISOString(),
  },
]

export function getDemoPrompt(date?: string): DailyPrompt {
  const targetDate = date || new Date().toISOString().split('T')[0]
  const found = DEMO_PROMPTS.find(p => p.date === targetDate)
  return found || DEMO_PROMPTS[0]
}

export function getAllDemoPrompts(): DailyPrompt[] {
  return DEMO_PROMPTS
}

// Supabase client
let supabase: SupabaseClient | null = null

function getSupabaseConfig() {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY
  return { supabaseUrl, supabaseAnonKey }
}

try {
  const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig()
  if (supabaseUrl && supabaseAnonKey) {
    supabase = createClient(supabaseUrl, supabaseAnonKey)
  } else {
    console.warn('Supabase environment variables not set. Using demo mode.')
  }
} catch (e) {
  console.warn('Supabase not configured. Using demo mode.')
}

export { supabase }// force rebuild
